import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/** Map i18next language codes to BCP47 og:locale format */
function toOgLocale(lang: string): string {
  const map: Record<string, string> = {
    en: 'en_US', es: 'es_US', 'es-ES': 'es_ES', 'pt-BR': 'pt_BR', 'pt-PT': 'pt_PT',
    'zh-CN': 'zh_CN', 'zh-TW': 'zh_TW', ja: 'ja_JP', ko: 'ko_KR', fr: 'fr_FR',
    de: 'de_DE', ru: 'ru_RU', tr: 'tr_TR', hi: 'hi_IN', id: 'id_ID', vi: 'vi_VN',
    th: 'th_TH', it: 'it_IT', nl: 'nl_NL', pl: 'pl_PL', uk: 'uk_UA', cs: 'cs_CZ',
    ro: 'ro_RO', hu: 'hu_HU', sv: 'sv_SE', da: 'da_DK', nb: 'nb_NO', fi: 'fi_FI',
    el: 'el_GR', ms: 'ms_MY', tl: 'tl_PH', bn: 'bn_BD', ca: 'ca_ES', hr: 'hr_HR',
    bg: 'bg_BG', sr: 'sr_RS', sk: 'sk_SK', sl: 'sl_SI', et: 'et_EE', lv: 'lv_LV',
    lt: 'lt_LT',
  };
  return map[lang] || 'en_US';
}

function setMeta(selector: string, attr: string, value: string) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

/**
 * Updates document-level SEO meta tags when the i18n language changes.
 * Call this once in App.tsx.
 */
export function useDocumentMeta() {
  const { t, i18n } = useTranslation('landing');
  const lang = i18n.language;

  useEffect(() => {
    const title = t('seo.title', { defaultValue: '' });
    const description = t('seo.description', { defaultValue: '' });
    const ogLocale = toOgLocale(lang);

    // html lang
    document.documentElement.lang = lang;

    // title
    if (title) document.title = title;

    // meta description
    if (description) setMeta('meta[name="description"]', 'content', description);

    // Open Graph
    setMeta('meta[property="og:locale"]', 'content', ogLocale);
    if (title) setMeta('meta[property="og:title"]', 'content', title);
    if (description) setMeta('meta[property="og:description"]', 'content', description);

    // Twitter
    if (title) setMeta('meta[name="twitter:title"]', 'content', title);
    if (description) setMeta('meta[name="twitter:description"]', 'content', description);
  }, [lang, t]);
}
